import { useTranslation } from "react-i18next";
import React from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AlertCircle, XCircle } from "lucide-react";

export interface ProductAvailabilityIssue {
  productId: string;
  description: string;
  type: "quantity_reduced" | "product_removed";
  originalQuantity?: number;
  adjustedQuantity?: number;
}

interface ProductAvailabilityModalProps {
  open: boolean;
  onClose: () => void;
  issues: ProductAvailabilityIssue[];
}

export const ProductAvailabilityModal: React.FC<
  ProductAvailabilityModalProps
> = ({ open, onClose, issues }) => {
  const { t } = useTranslation();
  const quantityIssues = issues.filter(
    (issue) => issue.type === "quantity_reduced",
  );
  const removedProducts = issues.filter(
    (issue) => issue.type === "product_removed",
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[90vw] sm:max-w-[80vw] md:max-w-[60vw] lg:max-w-[500px] w-full mx-4">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-amber-500" />
            {t("components.orders.productAvailabilityModal.productAvailabilityUpdates")}
          </DialogTitle>
          <DialogDescription>
            {t("components.orders.productAvailabilityModal.someProductsFromYour")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 max-h-96 overflow-y-auto">
          {quantityIssues.length > 0 && (
            <div className="space-y-2">
              <h4 className="font-medium text-sm text-amber-700">
                {t("components.orders.productAvailabilityModal.quantityAdjustments")}
              </h4>
              {quantityIssues.map((issue) => (
                <div
                  key={issue.productId}
                  className="bg-amber-50 border border-amber-200 rounded-md p-3"
                >
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                    <div className="text-sm">
                      <p className="font-medium text-amber-900">
                        {issue.description}
                      </p>
                      <p className="text-amber-700 mt-1">
                        {t("components.orders.productAvailabilityModal.quantityReducedFrom")} {issue.originalQuantity} to{" "}
                        {issue.adjustedQuantity} {t("components.orders.productAvailabilityModal.maximumAvailable")}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {removedProducts.length > 0 && (
            <div className="space-y-2">
              <h4 className="font-medium text-sm text-red-700">
                {t("components.orders.productAvailabilityModal.removedProducts")}
              </h4>
              {removedProducts.map((issue) => (
                <div
                  key={issue.productId}
                  className="bg-red-50 border border-red-200 rounded-md p-3"
                >
                  <div className="flex items-start gap-2">
                    <XCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                    <div className="text-sm">
                      <p className="font-medium text-red-900">
                        {issue.description}
                      </p>
                      <p className="text-red-700 mt-1">
                        {t("components.orders.productAvailabilityModal.thisProductIsNo")}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button onClick={onClose} className="w-full sm:w-auto">
            OK
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
